<template>
  <div class="expeditors-page">
    <div class="content-container">
      <div class="page-header">
        <img
          :src="expeditorsLogo"
          height="60"
          alt="Expeditors powered by Reelables logo"
        >
        <h1>{{ pageTitle }}</h1>
        <div class="customer-logo">
          <img
            v-if="customerLogo"
            :src="customerLogo"
            height="60"
            alt="Customer logo"
          >
        </div>
      </div>
      <ErrorMessage
        :error-message="errorMessage"
        is-large
      />
      <Loader
        :is-loading="!isValidProfile && isLoading"
        :color="colours.tealBright"
      />
      <div
        v-if="isValidProfile"
        class="page-content"
      >
        <div class="search-container">
          <v-form
            ref="form"
            class="form-wrapper"
            @submit.prevent="trackShipment"
          >
            <div class="inputs-container">
              <v-text-field
                v-model="form.trackingRef"
                name="trackingRef"
                placeholder="Enter shipment tracking reference"
                :rules="[required('Tracking reference')]"
                required
                clearable
                :color="colours.tealDark"
                autocomplete="off"
              />
            </div>
            <div class="button-container">
              <v-btn
                :loading="isLoading"
                dark
                :color="colours.gray1"
                @click="trackShipment"
              >
                Track shipment
              </v-btn>
            </div>
          </v-form>
        </div>

        <div
          v-if="hasResults"
          class="results-container"
        >
          <div class="section">
            <h2>Shipment Details</h2>
            <table
              v-if="shipmentDetail"
              class="header-table"
            >
              <tr><td>Shipment ID</td><td>{{ shipmentDetail.id }}</td></tr>
              <tr><td>Status</td><td>{{ shipmentDetail.status }}</td></tr>
              <tr><td>Route</td><td>{{ shipmentDetail.route }}</td></tr>
              <tr><td>Shipper</td><td>{{ shipmentDetail.shipper }}</td></tr>
              <tr><td>Consignee</td><td>{{ shipmentDetail.consignee }}</td></tr>
              <tr><td>Contents</td><td>{{ shipmentDetail.contents }}</td></tr>
            </table>
          </div>
          <div class="section">
            <h2>Map</h2>
            <div class="map-container">
              <MapWithPopup
                :locations="locations"
                show-all-data
                is-wide-popup
              />
            </div>
          </div>
          <div class="section">
            <h2>Timeline</h2>
            <table class="timeline-table">
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>Source</th>
                  <th>Event</th>
                  <th>Code</th>
                  <th>Remarks</th>
                  <th>Origin</th>
                  <th>Destination</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(event, index) in timeline"
                  :key="index"
                  :class="{
                    'highlight-row': event.source === 'Reelables',
                  }"
                >
                  <td>{{ event.timestamp }}</td>
                  <td>{{ event.source }}</td>
                  <td>{{ event.event }}</td>
                  <td>{{ event.code }}</td>
                  <td>{{ event.remarks }}</td>
                  <td>{{ event.origin }}</td>
                  <td>{{ event.destination }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import ExpeditorsLogo from '../../assets/images/tracking/expeditors/expeditors-logo.png'; // Example logo import
import ErrorMessage from '../../components/atoms/message/error-message';
import Loader from '../../components/atoms/loader/loader';
import MapWithPopup from '../../components/molecules/map/map-with-popup';

import rules from '../../mixins/input-validation-rules';

import TILogo from '../../assets/images/tracking/expeditors/ti.png';
import WDLogo from '../../assets/images/tracking/expeditors/wd.png';
import SDLogo from '../../assets/images/tracking/expeditors/sandisk.png';
import LenovoLogo from '../../assets/images/tracking/expeditors/lenovo.png';
import _3MLogo from '../../assets/images/tracking/expeditors/3M Logo.png';

import COLOURS from '../../styles/_updated-variables.scss';
import { icons, mapEntityTypes } from '../../constants';

const API_BASE_URL = 'https://3708wwljv0.execute-api.us-east-1.amazonaws.com/main/';
const API_KEY = 'c1OaJbSuqb23i7D8WH8Sj8OfRiPD0Cfd9ZUgQpmC';

const HTTP_METHODS = {
  GET: 'GET',
  POST: 'POST',
  PUT: 'PUT',
  DELETE: 'DELETE',
};

const CLIENT_LOGOS = {
  'ti.png': TILogo,
  'wd.png': WDLogo,
  'sandisk.png': SDLogo,
  'lenovo.png': LenovoLogo,
  '3m.png': _3MLogo,
};

// const COLOURS = {
//   // TEAL:
//   PRIMARY: '#007bff',
//   SECONDARY: '#6c757d',
//   SUCCESS: '#28a745',
//   DANGER: '#dc3545',
//   WARNING: '#ffc107',
//   INFO: '#17a2b8',
//   LIGHT: '#f8f9fa',
//   DARK: '#343a40',
// };

export default {
  components: {
    ErrorMessage,
    Loader,
    MapWithPopup,
  },
  mixins: [rules],
  data() {
    return {
      isLoading: true,
      error: '',
      isValidProfile: false,
      clientName: '',
      clientLogo: '',
      form: {
        trackingRef: this.$route.query.r,
      },
      shipmentDetail: null,
      timeline: [],
      locations: [],
    };
  },
  computed: {
    colours() { return COLOURS; },
    profileName() { return this.$route.query.n || ''; },
    profileKey() { return this.$route.query.k || ''; },
    missingParams() { return !this.profileName || !this.profileKey; },
    expeditorsLogo() { return ExpeditorsLogo; },
    customerLogo() { return this.clientLogo || ''; },
    pageTitle() { return `${this.clientName || ''} Shipment Tracking`; },
    errorMessage() {
      return this.missingParams
        ? `Missing required parameters: ${!this.profileName ? 'profileName' : ''} ${!this.profileKey ? 'profileKey' : ''}`
        : this.error;
    },
    hasResults() {
      return this.shipmentDetail;
    },
  },
  created() {
    if (!this.missingParams) {
      this.getProfile();
    }
  },
  methods: {
    clearShipment() {
      this.shipmentDetail = null;
      this.timeline = [];
      this.locations = [];
    },
    async trackShipment() {
      if (this.$refs.form.validate()) {
        this.clearShipment();
        // set query param
        this.$router.replace({
          query: {
            n: this.profileName,
            k: this.profileKey,
            r: this.form.trackingRef,
          },
        }).catch(() => {
          // pass - duplicate navigation error
        });
        const data = await this.callApi(HTTP_METHODS.POST, 'tracksheet', {
          profileName: this.profileName,
          profileKey: this.profileKey,
          shipment: this.form.trackingRef.trim(),
        });

        if (data) {
          this.shipmentDetail = data.header;
          this.timeline = data.timeline.map((event) => ({
            ...event,
            timestamp: event.timestamp ? new Date(event.timestamp).toLocaleString() : '',
          }));
          this.locations = data.locations.map((location) => ({
            ...location,
            properties: {
              type: mapEntityTypes.asset,
              icon: icons.asset,
              name: location.asset,
              // asset: { name: 'Asset', value: location.asset },
              firstSeen: {
                name: 'First Seen',
                value: location.firstSeen ? new Date(location.firstSeen).toLocaleString() : '',
              },
              lastSeen: {
                name: 'Last Seen',
                value: location.lastSeen ? new Date(location.lastSeen).toLocaleString() : '',
              },
              // count: { name: 'Count', value: location.count },
            },
          }));
        }
      }
    },
    async getProfile() {
      const profile = await this.callApi(HTTP_METHODS.POST, 'tracksheet/profile', {
        profileName: this.profileName,
        profileKey: this.profileKey,
      });

      if (profile) {
        this.clientName = profile.clientName || '';
        this.clientLogo = profile.clientLogoUrl ? CLIENT_LOGOS[profile.clientLogoUrl] || '' : '';
        this.isValidProfile = !!this.clientName; // && !!this.clientLogo;
      }
    },
    async callApi(method, path, payload) {
      try {
        this.error = '';
        this.isLoading = true;
        const response = await fetch(`${API_BASE_URL}${path}`, {
          method,
          headers: {
            'x-api-key': API_KEY,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to fetch profile information.');
        }

        const data = await response.json();

        return data;
      } catch (error) {
        console.error('Error calling API:', error);
        this.error = error.message || 'An error occurred while fetching data.';
      } finally {
        this.isLoading = false;
      }

      return null;
    },

  },
};
</script>
<style lang="scss" scoped>
@import '@/styles/_updated-variables.scss';

html, body {
  margin: 0;
}

.expeditors-page {
  font-family: Helvetica-Neue, Arial, sans-serif;
  color: $dark;
  background-color: #f0f0f0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  min-height: 100vh;
  margin-top: -20px;
  .content-container {
    background-color: #fff;
    padding: 20px;
    max-width: 1200px;
    margin: 0 auto;
    border-radius: 10px;
  }
  .page-header {
    display: grid;
    // grid-template-columns: repeat(3, 1fr);
    grid-template-columns: 1fr 2fr 1fr;
    grid-gap: 10px;
    align-items: center;
    // display: flex;
    // flex-direction: row;
    // align-items: center;
    // justify-content: space-between;
    margin: 20px 0 ;
    h1 {
      font-size: 1.5rem;
      color: $tealDark;
      text-align: center;
    }
    .customer-logo {
      text-align: right;
    }
  }
  .page-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 20px 0;

    .search-container {
      width: 100%;
      padding-bottom: 20px;
      background-color: lighten($blue, 32%);
      border-radius: 4px;
      .form-wrapper {
        max-width: 500px;
        margin: 0 auto;
        display: flex;
        flex-direction: row;
        align-items: flex-end;
        justify-content: center;
        .inputs-container {
          width: 400px;
          margin-right: 10px;
        }
      }
    }

    .results-container {
      width: 100%;
      h2 {
        color: $tealBright;
        margin-top: 30px;
        margin-bottom: 15px;
        border-bottom: 1px solid #eee;
        padding-bottom: 10px;
      }
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 20px;

      tr {
        &.highlight-row {
          background-color: lighten($tealLight, 12%);
          // color: white;
        }
      }
    }
    table th, table td {
      padding: 11px;
      border: 1px solid #ddd;
      text-align: left;
    }
    table th {
      background-color: $tealDark;
      color: white;
      font-weight: bold;
    }
    .header-table td:first-child {
      background-color: $tealDark;
      color: white;
      font-weight: bold;
      width: 150px;
    }

    .map-container {
      height: 400px;
      width: 100%;
      margin-top: 20px;
      border-radius: 4px;
    }
  }
}
</style>
